import { Box, BoxProps, Checkbox, CheckboxGroup } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";

interface Props<T extends ReferenceModel> {
  /** The unfiltered collection of items */
  allItems?: T[];
  /** An optionally filtered collection of items */
  items: T[];
  name?: string;
  onChange?: (selectedItems: T[]) => void;
  onToggle?: (item: T) => void;
  value: T[];
  maxH?: BoxProps["maxH"];
}

export function Multiselect<T extends ReferenceModel>({
  allItems,
  items,
  name,
  onChange,
  onToggle,
  value,
  maxH,
}: Props<T>) {
  const memoizedAllItems = useMemo(() => allItems ?? items, [allItems, items]);

  const toggleItem = useCallback(
    (item: T) => {
      const selected = new Set(value.map((i) => i.id));

      if (selected.has(item.id)) {
        selected.delete(item.id);
      } else {
        selected.add(item.id);
      }

      onToggle?.(item);
      onChange?.(memoizedAllItems.filter((i) => selected.has(i.id)));
    },
    [value, onChange, memoizedAllItems, onToggle]
  );

  return (
    <Box maxH={maxH} overflowY="auto">
      <CheckboxGroup>
        {items.map((item) => (
          <Box key={`item-${item.name}`}>
            <Checkbox
              isChecked={value.includes(item)}
              name={name ? `${name}-item-${item.id}` : undefined}
              onChange={() => toggleItem(item)}
              width="100%"
            >
              {item.name}
            </Checkbox>
          </Box>
        ))}
      </CheckboxGroup>
    </Box>
  );
}
